import React, { useState } from "react";
import { api, upload } from "./api";
import { useProblems, FieldProblem, invalid } from "./form-check";
import { stamp } from "./Pass";

/**
 * The staff import (build plan §10.1 #7; System Admin): the Cognito Forms
 * export, medical subset only. Preview first: what is new, changed,
 * unchanged, and any record edited in the app since its import, field by
 * field. Ticking a row overwrites it with the imported values; an unticked
 * row keeps what the app has. Importing the same file again changes nothing.
 */

interface PlanRow {
  rowKey: string;
  action: "create" | "update" | "unchanged" | "conflict";
  name: string;
  staffKey: string;
  diff: { field: string; label: string; imported: string; app: string; editedInApp: boolean }[];
  editedInAppUtc: string;
}
interface Plan {
  sourceForm: string;
  role: string;
  rows: PlanRow[];
  skipped: { rowNumber: number; reason: string }[];
  counts: Record<string, number>;
}
interface Result extends Plan {
  written: { created: string[]; updated: string[]; overwritten: string[] };
}

const SKIP_LABEL: Record<string, string> = { NotComplete: "not completed", MissingId: "no entry id", Unparseable: "a date that can't be read" };

export function StaffImport({ onDone }: { onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);
  async function preview() {
    setError(null);
    setDone(null);
    if (!file) return setError("Choose the .xlsx or .csv file exported from Cognito Forms.");
    setBusy(true);
    const r = await upload<{ plan: Plan }>(`/api/staff/import/preview?name=${encodeURIComponent(file.name)}`, file);
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "The file could not be read.");
    setPlan(r.data.plan);
    setTicked(new Set());
  }
  async function run() {
    if (!file || !plan) return;
    setBusy(true);
    setError(null);
    const r = await upload<{ result: Result }>(`/api/staff/import?name=${encodeURIComponent(file.name)}&overwrite=${encodeURIComponent(JSON.stringify([...ticked]))}`, file);
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not imported.");
    const w = r.data.result.written;
    const kept = r.data.result.counts.conflict - w.overwritten.length;
    setDone(`Imported: ${w.created.length} new, ${w.updated.length} updated, ${w.overwritten.length} overwritten; ${kept} kept as edited in the app; ${r.data.result.counts.unchanged} unchanged.`);
    setPlan(null);
    setFile(null);
    setInputKey((n) => n + 1);
    onDone();
  }
  const by = (a: PlanRow["action"]) => plan?.rows.filter((r) => r.action === a) ?? [];
  return (
    <section className="section-card">
      <h3 className="mb-1 text-base font-semibold">Import from the staff application export</h3>
      <p className="text-sm text-slate-600">The .xlsx or .csv file downloaded from Cognito Forms (senior or junior staff). Only the medical subset is read; everything else in the file is discarded.</p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div><label htmlFor="si-file" className="field-label">Export file</label><input key={inputKey} id="si-file" type="file" accept=".xlsx,.csv" className="field-input" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPlan(null); setDone(null); }} /></div>
        <button type="button" className="btn-secondary min-h-11" disabled={busy || !file} onClick={() => void preview()}>Preview the import</button>
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      {done && <p className="mt-2 text-sm font-medium text-emerald-800" role="status">{done}</p>}
      {plan && (
        <div className="mt-3 space-y-3">
          <p className="text-sm">From <span className="font-medium">{plan.sourceForm}</span> ({plan.role}): <span className="font-medium">{plan.counts.create}</span> new · <span className="font-medium">{plan.counts.update}</span> changed · <span className="font-medium">{plan.counts.unchanged}</span> unchanged · <span className="font-medium">{plan.counts.conflict}</span> edited in the app · {plan.counts.skipped} skipped</p>
          {by("conflict").length > 0 && (
            <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-3">
              <p className="text-sm font-semibold text-amber-950">Edited in the app since the last import: tick a record to overwrite it with the imported values. Unticked, it keeps what the app has.</p>
              {by("conflict").map((r) => (
                <div key={r.rowKey} className="mt-2 rounded border border-amber-300 bg-white p-2">
                  <label className="flex min-h-11 items-center gap-2 text-sm font-medium">
                    <input type="checkbox" checked={ticked.has(r.rowKey)} onChange={(e) => { const t = new Set(ticked); if (e.target.checked) t.add(r.rowKey); else t.delete(r.rowKey); setTicked(t); }} />
                    Overwrite {r.name} ({r.staffKey}) with the imported values
                  </label>
                  {r.editedInAppUtc && <p className="text-xs text-slate-600">Last edited in the app {stamp(r.editedInAppUtc)}.</p>}
                  <div className="overflow-x-auto">
                    <table className="mt-1 w-full text-left text-sm">
                      <thead><tr className="text-xs text-slate-500"><th className="py-1 pr-2">Field</th><th className="py-1 pr-2">Imported value</th><th className="py-1">App value</th></tr></thead>
                      <tbody>{r.diff.map((d) => <tr key={d.field} className="border-t border-slate-100 align-top"><td className="py-1 pr-2">{d.label}{d.editedInApp && <span className="ml-1 rounded bg-amber-200 px-1 text-xs">changed in the app</span>}</td><td className="py-1 pr-2 break-words">{d.imported || "—"}</td><td className="py-1 break-words">{d.app || "—"}</td></tr>)}</tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
          {(["create", "update", "unchanged"] as const).map((a) => by(a).length > 0 && (
            <details key={a} className="rounded-md border border-slate-200 p-2 text-sm">
              <summary className="min-h-11 cursor-pointer py-2">{a === "create" ? "New" : a === "update" ? "Changed (updated)" : "Unchanged (nothing written)"}: {by(a).length}</summary>
              <ul className="mt-1 space-y-1">{by(a).map((r) => <li key={r.rowKey}>{r.name}{r.staffKey ? ` (${r.staffKey})` : ""}{r.diff.length ? `: ${r.diff.map((d) => d.label).join(", ")}` : ""}</li>)}</ul>
            </details>
          ))}
          {plan.skipped.length > 0 && <p className="text-xs text-slate-600">Skipped: {plan.skipped.map((s) => `row ${s.rowNumber} (${SKIP_LABEL[s.reason] ?? s.reason})`).join(", ")}.</p>}
          <button type="button" className="btn-primary min-h-11" disabled={busy || (plan.counts.create + plan.counts.update + ticked.size === 0)} onClick={() => void run()}>Import</button>
          {plan.counts.create + plan.counts.update + ticked.size === 0 && <p className="text-xs text-slate-600">Nothing to write: every record is unchanged{plan.counts.conflict ? " or kept as edited in the app" : ""}.</p>}
        </div>
      )}
    </section>
  );
}

const MANUAL: [string, string, string][] = [
  ["FirstName", "First name", "text"], ["LastName", "Last name", "text"], ["DateOfBirth", "Date of birth", "date"], ["Email", "Email", "email"], ["Phone", "Phone", "tel"],
  ["EmergencyContactName", "Emergency contact", "text"], ["EmergencyContactPhone", "Emergency contact phone", "tel"], ["Insurer", "Insurer", "text"], ["InsurancePolicyNumber", "Policy number", "text"],
  ["AllergiesText", "Allergies", "text"], ["MedicationsText", "Medications", "text"],
];

/** Manual add, for a volunteer who never completed the form (SourceForm=Manual). */
export function StaffManualAdd({ onDone }: { onDone: () => void }) {
  const blank = () => ({ ...Object.fromEntries(MANUAL.map(([k]) => [k, ""])), Role: "Other" }) as Record<string, string>;
  const [f, setF] = useState<Record<string, string>>(blank());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const { problems, check, reset } = useProblems([
    ["sm-FirstName", !f.FirstName.trim(), "Write the first name."],
    ["sm-LastName", !f.LastName.trim(), "Write the last name."],
  ]);
  async function add() {
    setError(null);
    setFlash(null);
    if (!check()) return;
    setBusy(true);
    const r = await api<{ staff: { staffKey: string } }>("POST", "/api/staff", f);
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not added.");
    setFlash(`Added ${f.LastName}, ${f.FirstName} as ${r.data.staff.staffKey}`);
    setF(blank());
    reset();
    onDone();
  }
  return (
    <details className="section-card">
      <summary className="flex min-h-11 cursor-pointer items-center"><h3 className="text-base font-semibold">Add a staff member by hand</h3></summary>
      <p className="mt-2 text-sm text-slate-600">For a volunteer who never completed the staff application.</p>
      <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {MANUAL.map(([k, label, type]) => (
          <div key={k}><label htmlFor={`sm-${k}`} className="field-label">{label}</label><input id={`sm-${k}`} type={type} className="field-input" {...invalid(`sm-${k}`, problems)} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /><FieldProblem id={`sm-${k}`} problems={problems} /></div>
        ))}
        <div><label htmlFor="sm-Role" className="field-label">Role</label><select id="sm-Role" className="field-input min-h-11" value={f.Role} onChange={(e) => setF({ ...f, Role: e.target.value })}>{["Senior", "Junior", "Other"].map((r) => <option key={r}>{r}</option>)}</select></div>
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" className="btn-secondary min-h-11" disabled={busy} onClick={() => void add()}>Add the staff member</button>
        {flash && <span className="text-sm font-medium text-emerald-800" role="status">{flash}</span>}
      </div>
    </details>
  );
}
